<template>
  <div class="bereiche-invitation-codes-view container py-4">
    <div v-if="loading" class="text-center my-5">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Bereich wird geladen...</span>
      </div>
    </div>

    <div v-else-if="error" class="alert alert-danger" role="alert">
      <font-awesome-icon icon="exclamation-triangle" /> {{ error }}
    </div>

    <div v-else-if="selectedTeam">
      <div class="bereich-invitation-codes">
        <div class="d-flex justify-content-between align-items-center mb-4">
          <h2>Einladungscodes</h2>
          <button class="btn btn-outline-secondary" @click="goBack">
            <font-awesome-icon icon="arrow-left" /> Zurück
          </button>
        </div>
        <!-- Breadcrumb -->
        <nav aria-label="breadcrumb" class="mb-4">
          <ol class="breadcrumb">
            <li class="breadcrumb-item">
              <router-link :to="{ name: 'teams-list' }" class="text-decoration-none">
                Bereiche
              </router-link>
            </li>
            <li class="breadcrumb-item">
              <router-link
                :to="{ name: 'teams-detail', params: { id: $route.params.id } }"
                class="text-decoration-none"
              >
                Quittungsverwaltung
              </router-link>
            </li>
            <li class="breadcrumb-item">
              <router-link
                :to="{ name: 'teams-settings', params: { id: $route.params.id } }"
                class="text-decoration-none"
              >
                Bereich-Einstellungen
              </router-link>
            </li>
            <li class="breadcrumb-item active" aria-current="page">Einladungscodes</li>
          </ol>
        </nav>
        <!-- Erklärung -->
        <div class="row mb-4">
          <div class="col-12">
            <div class="alert alert-info" role="alert">
              <h6 class="alert-heading">
                <font-awesome-icon icon="info-circle" class="me-2" />
                Was sind Einladungscodes?
              </h6>
              <p class="mb-2">
                Einladungscodes ermöglichen es dir, neue Mitglieder in deinen Bereich einzuladen. 
                Du kannst entweder automatisch generierte Codes nutzen oder eigene, benutzerdefinierte Codes erstellen.
              </p>
              <hr>
              <div class="row">
                <div class="col-md-6">
                  <h6 class="mb-2">So funktioniert's:</h6>
                  <ol class="mb-0 small">
                    <li>Erstelle einen Einladungscode</li>
                    <li>Teile den Code mit potenziellen Bereichsmitgliedern</li>
                    <li>Diese können den Code verwenden, um dem Bereich beizutreten</li>
                    <li>Du kannst Beitrittsanfragen genehmigen oder ablehnen</li>
                  </ol>
                </div>
                <div class="col-md-6">
                  <h6 class="mb-2">Hinweise:</h6>
                  <ul class="mb-0 small">
                    <li>Codes können optional ein Ablaufdatum haben</li>
                    <li>Du kannst Codes jederzeit deaktivieren</li>
                    <li>Alle Beitrittsanfragen müssen von dir genehmigt werden</li>
                    <li>Codes sind eindeutig und können nicht doppelt verwendet werden</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="row">
          <!-- Code erstellen -->
          <div class="col-lg-12 mb-4">
            <div class="card h-100">
              <div class="card-header">
                <h5 class="mb-0">Neuen Einladungscode erstellen</h5>
              </div>
              <div class="card-body">
                <form @submit.prevent="createInvitationCode">
                  <div class="mb-3">
                    <div class="form-check form-switch">
                      <input
                        class="form-check-input"
                        type="checkbox"
                        id="useCustomCode"
                        v-model="form.useCustomCode"
                      />
                      <label class="form-check-label" for="useCustomCode">
                        Eigenen Code verwenden
                      </label>
                    </div>
                  </div>

                  <div v-if="form.useCustomCode" class="mb-3">
                    <label for="customCode" class="form-label">Einladungscode</label>
                    <input
                      type="text"
                      class="form-control"
                      id="customCode"
                      v-model="form.customCode"
                      placeholder="z.B. BEREICHALPHA2023"
                      :class="{ 'is-invalid': errors.customCode }"
                    />
                    <div class="invalid-feedback" v-if="errors.customCode">
                      {{ errors.customCode }}
                    </div>
                    <div class="form-text">
                      Code muss eindeutig sein. Verwende nur Buchstaben und Zahlen.
                    </div>
                  </div>

                  <div class="mb-3">
                    <div class="form-check form-switch">
                      <input
                        class="form-check-input"
                        type="checkbox"
                        id="hasExpiration"
                        v-model="form.hasExpiration"
                      />
                      <label class="form-check-label" for="hasExpiration">
                        Ablaufdatum festlegen
                      </label>
                    </div>
                  </div>

                  <div v-if="form.hasExpiration" class="mb-3">
                    <label for="expiresAt" class="form-label">Ablaufdatum</label>
                    <input
                      type="datetime-local"
                      class="form-control"
                      id="expiresAt"
                      v-model="form.expiresAt"
                      :min="minDate"
                      :class="{ 'is-invalid': errors.expiresAt }"
                    />
                    <div class="invalid-feedback" v-if="errors.expiresAt">
                      {{ errors.expiresAt }}
                    </div>
                  </div>

                  <div v-if="createError" class="alert alert-danger mb-3" role="alert">
                    <font-awesome-icon icon="exclamation-triangle" /> {{ createError }}
                  </div>

                  <div v-if="createSuccess" class="alert alert-success mb-3" role="alert">
                    <font-awesome-icon icon="check" /> Einladungscode wurde erfolgreich erstellt!
                  </div>

                  <div class="d-grid">
                    <button type="submit" class="btn btn-primary" :disabled="creating">
                      <span
                        v-if="creating"
                        class="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      <font-awesome-icon v-else icon="plus" /> Einladungscode erstellen
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>

          <!-- Bestehende Einladungscodes -->
          <div class="col-12 mb-4">
            <div class="card">
              <div class="card-header d-flex justify-content-between align-items-center">
                <h5 class="mb-0">
                  {{ getCodesTitle() }}
                  <span v-if="codesPagination.total > 0" class="badge bg-primary ms-2">{{
                    codesPagination.total
                  }}</span>
                </h5>
                <div class="d-flex gap-2">
                  <button
                    class="btn btn-sm btn-outline-secondary"
                    @click="toggleCodesVisibility"
                    :title="showCodes ? 'Codes ausblenden' : 'Codes anzeigen'"
                  >
                    <font-awesome-icon :icon="showCodes ? 'eye-slash' : 'eye'" />
                    {{ showCodes ? 'Ausblenden' : 'Anzeigen' }}
                  </button>
                  <button
                    class="btn btn-sm btn-outline-primary"
                    @click="loadInvitationCodes"
                    :disabled="loadingCodes"
                  >
                    <font-awesome-icon icon="sync" :class="{ 'fa-spin': loadingCodes }" />
                    Aktualisieren
                  </button>
                </div>
              </div>
              <div class="card-body">
                <!-- Filter und Suche für Codes -->
                <div class="row g-3 mb-3">
                  <div class="col-md-4">
                    <label for="codesSearch" class="form-label">Code suchen</label>
                    <div class="input-group">
                      <input
                        id="codesSearch"
                        type="text"
                        class="form-control"
                        placeholder="Einladungscode suchen..."
                        v-model="codesSearch"
                        @input="debounceCodesSearch"
                      />
                      <button
                        class="btn btn-outline-secondary"
                        type="button"
                        @click="clearCodesSearch"
                        v-if="codesSearch"
                      >
                        <font-awesome-icon icon="times" />
                      </button>
                    </div>
                  </div>
                  <div class="col-md-3">
                    <label for="codesStatus" class="form-label">Status</label>
                    <select
                      id="codesStatus"
                      class="form-select"
                      v-model="codesStatus"
                      @change="changeCodesStatus"
                    >
                      <option value="active">Aktive Codes</option>
                      <option value="expired">Abgelaufene Codes</option>
                      <option value="all">Alle Codes</option>
                    </select>
                  </div>
                  <div class="col-md-2">
                    <label for="codesLimit" class="form-label">Pro Seite</label>
                    <select
                      id="codesLimit"
                      class="form-select"
                      v-model="codesPagination.limit"
                      @change="changeCodesLimit"
                    >
                      <option value="5">5</option>
                      <option value="10">10</option>
                      <option value="20">20</option>
                      <option value="50">50</option>
                    </select>
                  </div>
                  <div class="col-md-3 d-flex align-items-end">
                    <small class="text-muted">
                      {{ codesPagination.total || 0 }} Code{{
                        codesPagination.total !== 1 ? 's' : ''
                      }}
                      gefunden
                    </small>
                  </div>
                </div>
                <div v-if="loadingCodes" class="text-center my-3">
                  <div class="spinner-border spinner-border-sm" role="status">
                    <span class="visually-hidden">Laden...</span>
                  </div>
                </div>

                <div v-else-if="codesError" class="alert alert-danger" role="alert">
                  <font-awesome-icon icon="exclamation-triangle" /> {{ codesError }}
                </div>

                <div v-else-if="invitationCodes.length === 0" class="text-center my-3">
                  <p class="text-muted">
                    {{ getEmptyCodesMessage() }}
                  </p>
                </div>

                <div v-else class="table-responsive">
                  <table class="table table-hover">
                    <thead>
                      <tr>
                        <th>Code</th>
                        <th>Typ</th>
                        <th>Erstellt am</th>
                        <th>Läuft ab</th>
                        <th>Erstellt von</th>
                        <th>Aktionen</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="code in invitationCodes" :key="code.id">
                        <td>
                          <code
                            class="bg-primary text-white px-2 py-1 rounded invitation-code"
                            :class="{ 'blur-code': !showCodes }"
                            @click="copyToClipboard(code.code)"
                            :title="
                              showCodes ? 'Klicken zum Kopieren' : 'Codes anzeigen um zu kopieren'
                            "
                            style="cursor: pointer"
                          >
                            {{ showCodes ? code.code : '••••••••' }}
                          </code>
                        </td>
                        <td>
                          <span
                            class="badge"
                            :class="code.isCustomCode ? 'bg-info' : 'bg-secondary'"
                          >
                            {{ code.isCustomCode ? 'Benutzerdefiniert' : 'Generiert' }}
                          </span>
                        </td>
                        <td>{{ formatDate(code.createdAt) }}</td>
                        <td>
                          <span
                            v-if="code.expiresAt"
                            :class="isExpired(code.expiresAt) ? 'text-danger' : 'text-muted'"
                          >
                            {{ formatDate(code.expiresAt) }}
                            <small v-if="isExpired(code.expiresAt)" class="text-danger">
                              (Abgelaufen)
                            </small>
                          </span>
                          <span v-else class="no-expiry-text">Kein Ablaufdatum</span>
                        </td>
                        <td>{{ getCreatorName(code.creator) }}</td>
                        <td>
                          <button
                            class="btn btn-sm btn-outline-danger"
                            @click="deactivateCode(code.id)"
                            :disabled="deactivatingCode === code.id"
                            title="Code deaktivieren"
                          >
                            <span
                              v-if="deactivatingCode === code.id"
                              class="spinner-border spinner-border-sm"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            <font-awesome-icon v-else icon="ban" />
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>

                  <!-- Pagination für Codes -->
                  <nav
                    v-if="codesPagination.totalPages > 1"
                    aria-label="Paginierung der Einladungscodes"
                    class="mt-3"
                  >
                    <ul class="pagination justify-content-center">
                      <li class="page-item" :class="{ disabled: codesPagination.page === 1 }">
                        <button
                          class="page-link"
                          @click="changeCodesPage(1)"
                          :disabled="codesPagination.page === 1"
                        >
                          <font-awesome-icon icon="angle-double-left" />
                        </button>
                      </li>
                      <li class="page-item" :class="{ disabled: codesPagination.page === 1 }">
                        <button
                          class="page-link"
                          @click="changeCodesPage(codesPagination.page - 1)"
                          :disabled="codesPagination.page === 1"
                        >
                          <font-awesome-icon icon="angle-left" />
                        </button>
                      </li>

                      <li
                        v-for="page in getCodesPageNumbers()"
                        :key="page"
                        class="page-item"
                        :class="{ active: page === codesPagination.page }"
                      >
                        <button class="page-link" @click="changeCodesPage(page)">{{ page }}</button>
                      </li>

                      <li
                        class="page-item"
                        :class="{ disabled: codesPagination.page === codesPagination.totalPages }"
                      >
                        <button
                          class="page-link"
                          @click="changeCodesPage(codesPagination.page + 1)"
                          :disabled="codesPagination.page === codesPagination.totalPages"
                        >
                          <font-awesome-icon icon="angle-right" />
                        </button>
                      </li>
                      <li
                        class="page-item"
                        :class="{ disabled: codesPagination.page === codesPagination.totalPages }"
                      >
                        <button
                          class="page-link"
                          @click="changeCodesPage(codesPagination.totalPages)"
                          :disabled="codesPagination.page === codesPagination.totalPages"
                        >
                          <font-awesome-icon icon="angle-double-right" />
                        </button>
                      </li>
                    </ul>
                  </nav>

                  <!-- Pagination Info für Codes -->
                  <div v-if="codesPagination.total > 0" class="text-center text-muted small mt-2">
                    Zeige {{ (codesPagination.page - 1) * codesPagination.limit + 1 }} bis
                    {{
                      Math.min(codesPagination.page * codesPagination.limit, codesPagination.total)
                    }}
                    von {{ codesPagination.total }} Codes
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Beitrittsanfragen -->
          <div class="col-12">
            <div class="card">
              <div class="card-header d-flex justify-content-between align-items-center">
                <h5 class="mb-0">
                  Beitrittsanfragen
                  <span v-if="requestsPagination.total > 0" class="badge bg-secondary ms-2">{{
                    requestsPagination.total
                  }}</span>
                </h5>
                <button
                  class="btn btn-sm btn-outline-primary"
                  @click="loadJoinRequests"
                  :disabled="loadingRequests"
                >
                  <font-awesome-icon icon="sync" :class="{ 'fa-spin': loadingRequests }" />
                  Aktualisieren
                </button>
              </div>
              <div class="card-body">
                <!-- Filter und Suche für Anfragen -->
                <div class="row g-3 mb-3">
                  <div class="col-md-4">
                    <label for="requestsSearch" class="form-label">Suchen</label>
                    <div class="input-group">
                      <input
                        id="requestsSearch"
                        type="text"
                        class="form-control"
                        placeholder="Name, E-Mail oder Code suchen..."
                        v-model="requestsSearch"
                        @input="debounceRequestsSearch"
                      />
                      <button
                        class="btn btn-outline-secondary"
                        type="button"
                        @click="clearRequestsSearch"
                        v-if="requestsSearch"
                      >
                        <font-awesome-icon icon="times" />
                      </button>
                    </div>
                  </div>
                  <div class="col-md-3">
                    <label for="requestsFilter" class="form-label">Status</label>
                    <select
                      id="requestsFilter"
                      class="form-select"
                      v-model="requestsFilter"
                      @change="loadJoinRequests"
                    >
                      <option value="">Alle Anfragen</option>
                      <option value="pending">Ausstehend</option>
                      <option value="approved">Genehmigt</option>
                      <option value="rejected">Abgelehnt</option>
                    </select>
                  </div>
                  <div class="col-md-2">
                    <label for="requestsLimit" class="form-label">Pro Seite</label>
                    <select
                      id="requestsLimit"
                      class="form-select"
                      v-model="requestsPagination.limit"
                      @change="changeRequestsLimit"
                    >
                      <option value="5">5</option>
                      <option value="10">10</option>
                      <option value="20">20</option>
                      <option value="50">50</option>
                    </select>
                  </div>
                  <div class="col-md-3 d-flex align-items-end">
                    <small class="text-muted">
                      {{ requestsPagination.total || 0 }} Anfrage{{
                        requestsPagination.total !== 1 ? 'n' : ''
                      }}
                      gefunden
                    </small>
                  </div>
                </div>
                <div v-if="loadingRequests" class="text-center my-3">
                  <div class="spinner-border spinner-border-sm" role="status">
                    <span class="visually-hidden">Laden...</span>
                  </div>
                </div>

                <div v-else-if="requestsError" class="alert alert-danger" role="alert">
                  <font-awesome-icon icon="exclamation-triangle" /> {{ requestsError }}
                </div>

                <div v-else-if="joinRequests.length === 0" class="text-center my-3">
                  <p class="text-muted">
                    {{
                      requestsFilter || requestsSearch
                        ? 'Keine Anfragen entsprechen den Filterkriterien.'
                        : 'Keine Beitrittsanfragen vorhanden.'
                    }}
                  </p>
                </div>

                <div v-else class="table-responsive">
                  <table class="table table-hover">
                    <thead>
                      <tr>
                        <th>Nutzer</th>
                        <th>Einladungscode</th>
                        <th>Angefragt am</th>
                        <th>Status</th>
                        <th>Bearbeitet von</th>
                        <th>Aktionen</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="request in joinRequests" :key="request.id">
                        <td>
                          {{ getUserName(request.user) }}
                          <br />
                          <small class="text-muted">{{ request.user?.email }}</small>
                        </td>
                        <td>
                          <code>{{ request.invitation?.code }}</code>
                        </td>
                        <td>{{ formatDate(request.requestedAt) }}</td>
                        <td>
                          <span class="badge" :class="getStatusBadgeClass(request.status)">
                            {{ getStatusText(request.status) }}
                          </span>
                        </td>
                        <td>
                          <span v-if="request.processor">
                            {{ getUserName(request.processor) }}
                            <br />
                            <small class="text-muted">{{ formatDate(request.processedAt) }}</small>
                          </span>
                          <span v-else class="text-muted">-</span>
                        </td>
                        <td>
                          <div v-if="request.status === 'pending'" class="d-flex gap-1">
                            <button
                              class="btn btn-sm btn-success"
                              @click="approveRequest(request.id)"
                              :disabled="processingRequest === request.id"
                              title="Genehmigen"
                            >
                              <span
                                v-if="processingRequest === request.id"
                                class="spinner-border spinner-border-sm"
                                role="status"
                                aria-hidden="true"
                              ></span>
                              <font-awesome-icon v-else icon="check" />
                            </button>
                            <button
                              class="btn btn-sm btn-danger"
                              @click="showRejectModal(request)"
                              title="Ablehnen"
                            >
                              <font-awesome-icon icon="times" />
                            </button>
                          </div>
                          <div
                            v-else-if="request.status === 'rejected' && request.rejectionReason"
                            class="d-flex gap-1"
                          >
                            <button
                              class="btn btn-sm btn-outline-info"
                              @click="showRejectionReason(request)"
                              title="Ablehnungsgrund anzeigen"
                            >
                              <font-awesome-icon icon="eye" />
                            </button>
                          </div>
                          <span v-else class="text-muted">-</span>
                        </td>
                      </tr>
                    </tbody>
                  </table>

                  <!-- Pagination für Anfragen -->
                  <nav
                    v-if="requestsPagination.totalPages > 1"
                    aria-label="Paginierung der Beitrittsanfragen"
                    class="mt-3"
                  >
                    <ul class="pagination justify-content-center">
                      <li class="page-item" :class="{ disabled: requestsPagination.page === 1 }">
                        <button
                          class="page-link"
                          @click="changeRequestsPage(1)"
                          :disabled="requestsPagination.page === 1"
                        >
                          <font-awesome-icon icon="angle-double-left" />
                        </button>
                      </li>
                      <li class="page-item" :class="{ disabled: requestsPagination.page === 1 }">
                        <button
                          class="page-link"
                          @click="changeRequestsPage(requestsPagination.page - 1)"
                          :disabled="requestsPagination.page === 1"
                        >
                          <font-awesome-icon icon="angle-left" />
                        </button>
                      </li>

                      <li
                        v-for="page in getRequestsPageNumbers()"
                        :key="page"
                        class="page-item"
                        :class="{ active: page === requestsPagination.page }"
                      >
                        <button class="page-link" @click="changeRequestsPage(page)">
                          {{ page }}
                        </button>
                      </li>

                      <li
                        class="page-item"
                        :class="{
                          disabled: requestsPagination.page === requestsPagination.totalPages,
                        }"
                      >
                        <button
                          class="page-link"
                          @click="changeRequestsPage(requestsPagination.page + 1)"
                          :disabled="requestsPagination.page === requestsPagination.totalPages"
                        >
                          <font-awesome-icon icon="angle-right" />
                        </button>
                      </li>
                      <li
                        class="page-item"
                        :class="{
                          disabled: requestsPagination.page === requestsPagination.totalPages,
                        }"
                      >
                        <button
                          class="page-link"
                          @click="changeRequestsPage(requestsPagination.totalPages)"
                          :disabled="requestsPagination.page === requestsPagination.totalPages"
                        >
                          <font-awesome-icon icon="angle-double-right" />
                        </button>
                      </li>
                    </ul>
                  </nav>

                  <!-- Pagination Info -->
                  <div
                    v-if="requestsPagination.total > 0"
                    class="text-center text-muted small mt-2"
                  >
                    Zeige {{ (requestsPagination.page - 1) * requestsPagination.limit + 1 }} bis
                    {{
                      Math.min(
                        requestsPagination.page * requestsPagination.limit,
                        requestsPagination.total,
                      )
                    }}
                    von {{ requestsPagination.total }} Einträgen
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="alert alert-gradient-warning" role="alert">
      <font-awesome-icon icon="exclamation-triangle" /> Bereich nicht gefunden
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useTeamsStore } from '@/stores/teams'
import { useAuthStore } from '@/stores/auth'
import { teamService } from '@/api/teams'
import {
  showSuccess,
  showError,
  showRejectRequestModal,
  showRejectionReasonModal,
} from '@/services/notification'

const route = useRoute()
const router = useRouter()
const teamsStore = useTeamsStore()
const authStore = useAuthStore()

const { selectedTeam, loading, error } = storeToRefs(teamsStore)
const { user } = storeToRefs(authStore)

const { loadTeamById } = teamsStore

const isAdminOrCreator = computed(() => {
  if (!selectedTeam.value || !user.value) return false
  return selectedTeam.value.role === 'teamAdmin' || selectedTeam.value.creatorId === user.value.id
})


const form = reactive({
  useCustomCode: false,
  customCode: '',
  hasExpiration: false,
  expiresAt: '',
})

const errors = reactive({
  customCode: '',
  expiresAt: '',
})

const createError = ref(null)
const createSuccess = ref(false)
const creating = ref(false)


const invitationCodes = ref([])
const codesSearch = ref('')
const codesSearchTimeout = ref(null)
const codesError = ref(null)
const loadingCodes = ref(false)
const deactivatingCode = ref(null)
const codesStatus = ref('active')
const showCodes = ref(false)

const codesPagination = ref({
  page: 1,
  limit: 10,
  total: 0,
  totalPages: 0,
})


const joinRequests = ref([])
const requestsSearch = ref('')
const requestsSearchTimeout = ref(null)
const requestsError = ref(null)
const loadingRequests = ref(false)
const processingRequest = ref(null)
const requestsFilter = ref('')

const requestsPagination = ref({
  page: 1,
  limit: 10,
  total: 0,
  totalPages: 0,
})

const minDate = computed(() => {
  const now = new Date()
  now.setMinutes(now.getMinutes() - now.getTimezoneOffset())
  return now.toISOString().slice(0, 16)
})

const goBack = () => {
  const teamId = selectedTeam.value?.id || route.params.id
  router.push({ name: 'teams-settings', params: { id: teamId } })
}

const resetForm = () => {
  form.useCustomCode = false
  form.customCode = ''
  form.hasExpiration = false
  form.expiresAt = ''
  Object.keys(errors).forEach((key) => (errors[key] = ''))
  createError.value = null
  createSuccess.value = false
}

const validateForm = () => {
  Object.keys(errors).forEach((key) => (errors[key] = ''))
  let isValid = true

  if (form.useCustomCode && !form.customCode.trim()) {
    errors.customCode = 'Bitte gib einen Einladungscode ein'
    isValid = false
  }

  if (
    form.useCustomCode &&
    form.customCode.trim() &&
    !/^[A-Za-z0-9]+$/.test(form.customCode.trim())
  ) {
    errors.customCode = 'Code darf nur Buchstaben und Zahlen enthalten'
    isValid = false
  }

  if (form.hasExpiration && !form.expiresAt) {
    errors.expiresAt = 'Bitte wähle ein Ablaufdatum'
    isValid = false
  }

  if (form.hasExpiration && form.expiresAt && new Date(form.expiresAt) <= new Date()) {
    errors.expiresAt = 'Ablaufdatum muss in der Zukunft liegen'
    isValid = false
  }

  return isValid
}

const createInvitationCode = async () => {
  if (!validateForm()) return

  creating.value = true
  createError.value = null

  try {
    const options = {}

    if (form.useCustomCode) {
      options.customCode = form.customCode.trim()
      options.isCustom = true
    }

    if (form.hasExpiration) {
      options.expiresAt = form.expiresAt
    }

    await teamService.createInvitationCode(selectedTeam.value.id, options)
    showSuccess('Einladungscode wurde erfolgreich erstellt!')
    createSuccess.value = true
    resetForm()
    await loadInvitationCodes()

    setTimeout(() => {
      createSuccess.value = false
    }, 3000)
  } catch (err) {
    console.error('Fehler beim Erstellen des Einladungscodes:', err)
    createError.value = err.response?.data?.message || 'Einladungscode konnte nicht erstellt werden'
  } finally {
    creating.value = false
  }
}

const loadInvitationCodes = async () => {
  loadingCodes.value = true
  codesError.value = null

  try {
    const response = await teamService.getTeamInvitationCodes(
      selectedTeam.value.id,
      codesPagination.value.page,
      codesPagination.value.limit,
      codesSearch.value,
      codesStatus.value,
    )
    invitationCodes.value = response.invitations || []
    codesPagination.value = { ...codesPagination.value, ...response.pagination }
  } catch (err) {
    console.error('Fehler beim Laden der Einladungscodes:', err)

    if (err.response?.status === 403 || err.response?.status === 401) {
      showError('Du hast keine Berechtigung mehr für diesen Bereich')
      router.push('/teams')
      return
    }

    codesError.value = 'Einladungscodes konnten nicht geladen werden'
  } finally {
    loadingCodes.value = false
  }
}

const loadJoinRequests = async () => {
  loadingRequests.value = true
  requestsError.value = null

  try {
    const response = await teamService.getTeamJoinRequests(
      selectedTeam.value.id,
      requestsFilter.value || null,
      requestsPagination.value.page,
      requestsPagination.value.limit,
      requestsSearch.value,
    )
    joinRequests.value = response.requests || []
    requestsPagination.value = { ...requestsPagination.value, ...response.pagination }
  } catch (err) {
    console.error('Fehler beim Laden der Beitrittsanfragen:', err)

    if (err.response?.status === 403 || err.response?.status === 401) {
      showError('Du hast keine Berechtigung mehr für diesen Bereich')
      router.push('/teams')
      return
    }

    requestsError.value = 'Beitrittsanfragen konnten nicht geladen werden'
  } finally {
    loadingRequests.value = false
  }
}

const copyToClipboard = (text) => {
  navigator.clipboard
    .writeText(text)
    .then(() => {
      showSuccess('Code wurde in die Zwischenablage kopiert')
    })
    .catch(() => {
      showError('Code konnte nicht kopiert werden')
    })
}

const deactivateCode = async (codeId) => {
  deactivatingCode.value = codeId

  try {
    await teamService.deactivateInvitationCode(codeId)
    showSuccess('Einladungscode wurde deaktiviert')
    await loadInvitationCodes()
  } catch (err) {
    console.error('Fehler beim Deaktivieren des Codes:', err)
    showError('Code konnte nicht deaktiviert werden')
  } finally {
    deactivatingCode.value = null
  }
}

const approveRequest = async (requestId) => {
  processingRequest.value = requestId

  try {
    await teamService.approveJoinRequest(requestId)
    showSuccess('Beitrittsanfrage wurde genehmigt')
    await loadJoinRequests()
  } catch (err) {
    console.error('Fehler beim Genehmigen der Anfrage:', err)
    showError('Anfrage konnte nicht genehmigt werden')
  } finally {
    processingRequest.value = null
  }
}

const showRejectModal = async (request) => {
  const userName = getUserName(request.user)

  const result = await showRejectRequestModal(
    userName,
    async (reason) => {
      processingRequest.value = request.id
      try {
        await teamService.rejectJoinRequest(request.id, reason)
        showSuccess('Beitrittsanfrage wurde abgelehnt')
        await loadJoinRequests()
      } catch (err) {
        console.error('Fehler beim Ablehnen der Anfrage:', err)
        showError('Anfrage konnte nicht abgelehnt werden')
      } finally {
        processingRequest.value = null
      }
    },
    () => {
      
    },
  )
}

const showRejectionReason = async (request) => {
  const userName = getUserName(request.user)
  const rejectedAt = request.processedAt ? formatDate(request.processedAt) : null

  await showRejectionReasonModal(userName, request.rejectionReason, rejectedAt)
}

const formatDate = (dateString) => {
  if (!dateString) return 'Unbekannt'
  const date = new Date(dateString)
  return new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}

const isExpired = (dateString) => {
  if (!dateString) return false
  return new Date(dateString) < new Date()
}

const getCreatorName = (creator) => {
  if (!creator) return 'Unbekannt'
  return (
    `${creator.firstName || ''} ${creator.lastName || ''}`.trim() || creator.email || 'Unbekannt'
  )
}

const getUserName = (user) => {
  if (!user) return 'Unbekannt'
  return `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || 'Unbekannt'
}

const getStatusText = (status) => {
  const statusMap = {
    pending: 'Ausstehend',
    approved: 'Genehmigt',
    rejected: 'Abgelehnt',
  }
  return statusMap[status] || status
}

const getStatusBadgeClass = (status) => {
  const classMap = {
    pending: 'bg-warning',
    approved: 'bg-success',
    rejected: 'bg-danger',
  }
  return classMap[status] || 'bg-secondary'
}


const changeRequestsPage = (page) => {
  if (page >= 1 && page <= requestsPagination.value.totalPages) {
    requestsPagination.value.page = page
    loadJoinRequests()
  }
}

const getRequestsPageNumbers = () => {
  const current = requestsPagination.value.page
  const total = requestsPagination.value.totalPages

  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1)
  }

  if (current <= 3) {
    return [1, 2, 3, 4, 5]
  }

  if (current >= total - 2) {
    return [total - 4, total - 3, total - 2, total - 1, total]
  }

  return [current - 2, current - 1, current, current + 1, current + 2]
}

const debounceRequestsSearch = () => {
  if (requestsSearchTimeout.value) {
    clearTimeout(requestsSearchTimeout.value)
  }

  requestsSearchTimeout.value = setTimeout(() => {
    requestsPagination.value.page = 1
    loadJoinRequests()
  }, 500)
}

const clearRequestsSearch = () => {
  requestsSearch.value = ''
  requestsPagination.value.page = 1
  loadJoinRequests()
}

const changeRequestsLimit = () => {
  requestsPagination.value.page = 1
  loadJoinRequests()
}


const changeCodesPage = (page) => {
  if (page >= 1 && page <= codesPagination.value.totalPages) {
    codesPagination.value.page = page
    loadInvitationCodes()
  }
}

const getCodesPageNumbers = () => {
  const current = codesPagination.value.page
  const total = codesPagination.value.totalPages

  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1)
  }

  if (current <= 3) {
    return [1, 2, 3, 4, 5]
  }

  if (current >= total - 2) {
    return [total - 4, total - 3, total - 2, total - 1, total]
  }

  return [current - 2, current - 1, current, current + 1, current + 2]
}

const changeCodesLimit = () => {
  codesPagination.value.page = 1
  loadInvitationCodes()
}

const debounceCodesSearch = () => {
  if (codesSearchTimeout.value) {
    clearTimeout(codesSearchTimeout.value)
  }

  codesSearchTimeout.value = setTimeout(() => {
    codesPagination.value.page = 1
    loadInvitationCodes()
  }, 500)
}

const clearCodesSearch = () => {
  codesSearch.value = ''
  codesPagination.value.page = 1
  loadInvitationCodes()
}

const changeCodesStatus = () => {
  codesPagination.value.page = 1
  loadInvitationCodes()
}

const toggleCodesVisibility = () => {
  showCodes.value = !showCodes.value
}

const getCodesTitle = () => {
  const statusMap = {
    active: 'Aktive Einladungscodes',
    expired: 'Abgelaufene Einladungscodes',
    all: 'Alle Einladungscodes',
  }
  return statusMap[codesStatus.value] || 'Einladungscodes'
}

const getEmptyCodesMessage = () => {
  if (codesSearch.value) {
    return 'Keine Codes entsprechen der Suche.'
  }

  const statusMap = {
    active: 'Keine aktiven Einladungscodes vorhanden.',
    expired: 'Keine abgelaufenen Einladungscodes vorhanden.',
    all: 'Keine Einladungscodes vorhanden.',
  }
  return statusMap[codesStatus.value] || 'Keine Einladungscodes vorhanden.'
}

onMounted(async () => {
  const teamId = route.params.id
  if (teamId) {
    const team = await loadTeamById(teamId)
    if (!team) {
      router.push({ name: 'teams-list' })
      return
    }

    if (!isAdminOrCreator.value) {
      router.push({ name: 'teams-detail', params: { id: teamId } })
      return
    }

    
    loadInvitationCodes()
    loadJoinRequests()
  } else {
    router.push({ name: 'teams-list' })
  }
})

watch(
  () => route.params.id,
  async (newId) => {
    if (newId && newId !== selectedTeam.value?.id) {
      const team = await loadTeamById(newId)
      if (!team) {
        router.push({ name: 'teams-list' })
        return
      }

      if (!isAdminOrCreator.value) {
        router.push({ name: 'teams-detail', params: { id: newId } })
        return
      }

      
      loadInvitationCodes()
      loadJoinRequests()
    }
  },
)
</script>

<style scoped>
.invitation-code {
  font-family: 'Courier New', monospace;
  font-size: 0.9rem;
  font-weight: bold;
  letter-spacing: 1px;
}

.blur-code {
  filter: blur(3px);
  transition: filter 0.3s ease;
}

.no-expiry-text {
  color: #6c757d;
  font-style: italic;
}
</style>
